import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification, createSuccessNotification } from './common/NotificationSystem';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionButton } from './common/PermissionButton';
import { NamingConventionRuleForm } from './NamingConventionRuleForm';
import { NamingConventionAccountAttachment } from './NamingConventionAccountAttachment';
import { NamingConventionListResponse, NamingConventionAttachmentsResponse } from '../types/tagops';

export const NamingConventions: React.FC = () => {
  const { showNotification } = useNotifications();
  const { isAdministrator, isPowerUser } = usePermissions();
  const [rules, setRules] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [selectedRule, setSelectedRule] = useState<string | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await tagopsService.listNamingConventions();
      setRules(response.naming_conventions || []);
    } catch (error: any) {
      console.error('Error loading naming conventions:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to load naming conventions'));
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async () => {
    try {
      const response = await tagopsService.listNamingConventionAttachments();
      setAttachments(response.naming_convention_attachments || {});
    } catch (error: any) {
      console.error('Error loading attachments:', error);
      // Don't show error notification for attachments, just log it
    }
  };

  useEffect(() => {
    loadRules();
    loadAttachments();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadRules(), loadAttachments()]);
    setRefreshing(false);
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const response = await tagopsService.recalculateNamingConvention();
      showNotification(createSuccessNotification(
        'Recalculation Started',
        response.message || 'Naming convention recalculation started successfully'
      ));
    } catch (error: any) {
      console.error('Error recalculating naming convention:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to start naming convention recalculation'));
    } finally {
      setRecalculating(false);
    }
  };

  const handleCreate = () => {
    setSelectedRule(null);
    setShowCreateDialog(true);
  };

  const handleEdit = (ruleName: string) => {
    setSelectedRule(ruleName);
    setShowEditDialog(true);
  };

  const handleDelete = (ruleName: string) => {
    setRuleToDelete(ruleName);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      setDeleting(ruleToDelete);
      await tagopsService.deleteNamingConvention(ruleToDelete);
      showNotification(createSuccessNotification('Success', 'Naming convention deleted successfully'));
      setShowDeleteDialog(false);
      setRuleToDelete(null);
      await loadRules();
      await loadAttachments();
    } catch (error: any) {
      console.error('Error deleting naming convention:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to delete naming convention'));
    } finally {
      setDeleting(null);
    }
  };

  const handleManageAttachments = (ruleName: string) => {
    setSelectedRule(ruleName);
    setShowAttachmentDialog(true);
  };

  const handleFormClose = () => {
    setShowCreateDialog(false);
    setShowEditDialog(false);
    setSelectedRule(null);
  };

  const handleFormSuccess = async () => {
    handleFormClose();
    await loadRules();
    await loadAttachments();
  };

  const handleAttachmentClose = () => {
    setShowAttachmentDialog(false);
    setSelectedRule(null);
  };

  const handleAttachmentSuccess = async () => {
    await loadAttachments();
  };

  const getAttachedAccountsCount = (ruleName: string): number => {
    return attachments[ruleName]?.length || 0;
  };

  const canModify = isAdministrator || isPowerUser;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Naming Conventions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            Refresh
          </Button>
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Recalculate Naming Convention Status
                </Typography>
                <Typography variant="caption" component="div">
                  This process analyzes all resources against defined naming convention rules. 
                  Depending on the number of resources, this may take several minutes to complete.
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  The calculation runs in the background. You can continue working while it processes.
                </Typography>
              </Box>
            }
            arrow
            placement="bottom"
          >
            <span>
              <PermissionButton
                variant="outlined"
                startIcon={<CalculateIcon />}
                onClick={handleRecalculate}
                permission="PowerUser"
                disabled={recalculating || loading}
              >
                Recalculate
              </PermissionButton>
            </span>
          </Tooltip>
          <PermissionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            permission="PowerUser"
          >
            Create Rule
          </PermissionButton>
        </Box>
      </Box>

      {loading ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={60} />
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              No naming convention rules found. Create your first rule to get started.
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Rule Name</strong></TableCell>
                <TableCell><strong>Attached Accounts</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((ruleName) => {
                const accountCount = getAttachedAccountsCount(ruleName);
                const attachedAccountIds = attachments[ruleName] || [];
                return (
                  <TableRow key={ruleName} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {ruleName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {accountCount > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`${accountCount} account${accountCount !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  Attached to:
                                </Typography>
                                {attachedAccountIds.map((accountId) => (
                                  <Typography key={accountId} variant="caption" display="block">
                                    {accountId}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleManageAttachments(ruleName)}
                              disabled={!canModify}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No accounts attached
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Manage Account Attachments">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleManageAttachments(ruleName)}
                              disabled={!canModify}
                              color="primary"
                            >
                              <LinkIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit Rule">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(ruleName)}
                              disabled={!canModify}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete Rule">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(ruleName)}
                              disabled={!canModify || deleting === ruleName}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <NamingConventionRuleForm
        open={showCreateDialog}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        mode="create"
      />

      {/* Edit Dialog */}
      <NamingConventionRuleForm
        open={showEditDialog}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        mode="edit"
        ruleName={selectedRule || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Naming Convention Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule &quot;{ruleToDelete}&quot;?
            {ruleToDelete && getAttachedAccountsCount(ruleToDelete) > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This rule is attached to {getAttachedAccountsCount(ruleToDelete)} account(s).
                You must detach it from all accounts before deleting.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleting !== null}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting !== null || (ruleToDelete ? getAttachedAccountsCount(ruleToDelete) > 0 : false)}
          >
            {deleting === ruleToDelete ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Account Attachment Dialog */}
      <NamingConventionAccountAttachment
        open={showAttachmentDialog}
        onClose={handleAttachmentClose}
        onSuccess={handleAttachmentSuccess}
        ruleName={selectedRule || undefined}
      />
    </Box>
  );
};
